import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const ApiDocsPage = () => {
    const navigate = useNavigate();

    return (
        <div style={styles.page}>
            <Helmet>
                <title>API Documentation — OpenAPI Spec | APILoadTest.com</title>
                <meta name="description" content="APILoadTest REST API documentation. Run load tests programmatically with API key authentication. OpenAPI 3.1 specification for CI/CD integration." />
                <meta name="keywords" content="APILoadTest API, load testing API, API documentation, OpenAPI spec, CI/CD load testing, programmatic load testing" />
                <link rel="canonical" href="https://www.apiloadtest.com/api-docs" />
                <meta property="og:title" content="API Documentation | APILoadTest.com" />
                <meta property="og:description" content="Run load tests programmatically. Full OpenAPI 3.1 specification." />
                <meta property="og:url" content="https://www.apiloadtest.com/api-docs" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerInner}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                    <nav style={styles.navLinks}>
                        <Link to="/#features" style={styles.navLink}>Features</Link>
                        <Link to="/#pricing" style={styles.navLink}>Pricing</Link>
                        <Link to="/blog" style={styles.navLink}>Blog</Link>
                    </nav>
                    <button style={styles.cta} onClick={() => navigate('/login')}>Get Started</button>
                </div>
            </header>

            {/* Breadcrumb */}
            <nav style={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/" style={styles.breadcrumbLink}>Home</Link>
                <span style={styles.sep}>/</span>
                <span style={styles.breadcrumbCurrent}>API Documentation</span>
            </nav>

            {/* Hero */}
            <section style={styles.hero}>
                <h1 style={styles.heroTitle}>API Documentation</h1>
                <p style={styles.heroSub}>Run load tests programmatically. Integrate into your CI/CD pipeline.</p>
            </section>

            {/* Content */}
            <main style={styles.main}>

                {/* Overview */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>Overview</h2>
                    <p style={styles.text}>
                        The APILoadTest API allows you to run load tests programmatically using API key authentication.
                        This enables integration with CI/CD pipelines, automated testing workflows, and custom tooling.
                    </p>
                    <div style={styles.infoBox}>
                        <strong>Base URL:</strong> <code style={styles.code}>https://api.apiloadtest.com</code>
                    </div>
                </section>

                {/* Authentication */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>Authentication</h2>
                    <p style={styles.text}>
                        Authenticate using a Bearer token with your API key. API keys start with <code style={styles.code}>alt_live_</code> and
                        can be generated from your dashboard.
                    </p>
                    <div style={styles.codeBlock}>
                        <div style={styles.codeHeader}>
                            <span style={styles.codeLang}>Header</span>
                        </div>
                        <pre style={styles.pre}><code>{`Authorization: Bearer alt_live_YOUR_API_KEY`}</code></pre>
                    </div>
                </section>

                {/* Endpoint */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>Run Load Test</h2>
                    <div style={styles.endpointBadge}>
                        <span style={styles.methodBadge}>POST</span>
                        <code style={styles.endpointUrl}>/api/api-test/run</code>
                    </div>
                    <p style={styles.text}>Run a load test against a target URL. Returns detailed performance metrics
                        including p90, p95, and p99 response times.</p>

                    <h3 style={styles.subTitle}>Request Body</h3>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Parameter</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Required</th>
                                <th style={styles.th}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><code style={styles.code}>url</code></td><td style={styles.td}>string</td><td style={styles.td}>✓</td><td style={styles.td}>Target URL to test</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>method</code></td><td style={styles.td}>string</td><td style={styles.td}>✓</td><td style={styles.td}>HTTP method: GET, POST, PUT, PATCH, DELETE, HEAD, OPTIONS</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>headers</code></td><td style={styles.td}>object</td><td style={styles.td}></td><td style={styles.td}>Custom request headers</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>body</code></td><td style={styles.td}>string</td><td style={styles.td}></td><td style={styles.td}>Request body for POST/PUT/PATCH</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>connections</code></td><td style={styles.td}>integer</td><td style={styles.td}></td><td style={styles.td}>Virtual users (1-999)</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>duration</code></td><td style={styles.td}>integer</td><td style={styles.td}></td><td style={styles.td}>Duration in seconds (1-300)</td></tr>
                        </tbody>
                    </table>

                    <h3 style={styles.subTitle}>Example Request</h3>
                    <div style={styles.codeBlock}>
                        <div style={styles.codeHeader}>
                            <span style={styles.codeLang}>cURL</span>
                        </div>
                        <pre style={styles.pre}><code>{`curl -X POST https://api.apiloadtest.com/api/api-test/run \\
  -H "Authorization: Bearer alt_live_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.example.com/v1/users",
    "method": "GET",
    "connections": 50,
    "duration": 30
  }'`}</code></pre>
                    </div>

                    <h3 style={styles.subTitle}>Example Response</h3>
                    <div style={styles.codeBlock}>
                        <div style={styles.codeHeader}>
                            <span style={styles.codeLang}>JSON</span>
                            <span style={styles.statusBadge}>200 OK</span>
                        </div>
                        <pre style={styles.pre}><code>{`{
  "results": {
    "totalRequests": 1482,
    "successfulRequests": 1482,
    "averageResponseTime": 127.4,
    "p90ResponseTime": 198.0,
    "p95ResponseTime": 245.0,
    "p99ResponseTime": 412.0,
    "requestsPerSecond": 49.4,
    "errorPercentage": 0.0,
    "testDuration": 30.0,
    "statusCodes": { "200": 1482 }
  },
  "creditsRemaining": 47
}`}</code></pre>
                    </div>

                    <h3 style={styles.subTitle}>Error Responses</h3>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><code style={styles.code}>400</code></td><td style={styles.td}>Validation error — missing or invalid parameters</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>402</code></td><td style={styles.td}>Insufficient credits — purchase more credit packs</td></tr>
                            <tr><td style={styles.td}><code style={styles.code}>403</code></td><td style={styles.td}>API key authentication required or invalid</td></tr>
                        </tbody>
                    </table>
                </section>

                {/* OpenAPI Spec */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>OpenAPI Specification</h2>
                    <p style={styles.text}>
                        Download the full OpenAPI 3.1 specification for use with code generators, API clients, or documentation tools.
                    </p>
                    <div style={styles.specLinks}>
                        <a href="/openapi.json" style={styles.specLink} target="_blank" rel="noopener noreferrer">
                            📄 openapi.json
                        </a>
                    </div>
                </section>
            </main>

            {/* CTA */}
            <section style={styles.ctaSection}>
                <h2 style={styles.ctaTitle}>Ready to integrate?</h2>
                <p style={styles.ctaSub}>Sign up, generate your API key, and start testing.</p>
                <button style={styles.ctaButton} onClick={() => navigate('/login')}>Get Your API Key →</button>
            </section>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerInner}>
                    <div style={styles.footerLinksRow}>
                        <a href="/terms" style={styles.footerLink}>Terms</a>
                        <a href="/privacy" style={styles.footerLink}>Privacy</a>
                        <a href="/refund" style={styles.footerLink}>Refund</a>
                        <a href="/faq" style={styles.footerLink}>FAQ</a>
                        <a href="/glossary" style={styles.footerLink}>Glossary</a>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com · All rights reserved</p>
                </div>
            </footer>

            <style>{`
                @media (max-width: 768px) {
                    .docs-nav-links { display: none !important; }
                }
            `}</style>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', background: '#F7F8FC', fontFamily: "'DM Sans', 'Inter', sans-serif" },
    header: { background: '#fff', padding: '16px 0', position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid #E4E5EF' },
    headerInner: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logoSection: { display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' },
    logoImage: { height: '40px', width: 'auto' },
    logoText: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: '700', color: '#0A0D1C' },
    navLinks: { display: 'flex', gap: '32px', className: 'docs-nav-links' },
    navLink: { color: '#3D3F52', textDecoration: 'none', fontSize: '15px', fontWeight: '500' },
    cta: { background: '#6366f1', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
    breadcrumb: { maxWidth: '900px', margin: '0 auto', padding: '24px', fontSize: '14px' },
    breadcrumbLink: { color: '#6366f1', textDecoration: 'none' },
    sep: { margin: '0 8px', color: '#8B8FA8' },
    breadcrumbCurrent: { color: '#8B8FA8' },
    hero: { textAlign: 'center', padding: '40px 24px 48px', maxWidth: '700px', margin: '0 auto' },
    heroTitle: { fontFamily: "'Fraunces', serif", fontSize: '48px', fontWeight: '900', color: '#0A0D1C', letterSpacing: '-2px', marginBottom: '16px' },
    heroSub: { fontSize: '18px', color: '#8B8FA8', lineHeight: '1.6' },
    main: { maxWidth: '900px', margin: '0 auto', padding: '0 24px 64px' },
    section: { marginBottom: '48px' },
    sectionTitle: { fontFamily: "'Fraunces', serif", fontSize: '28px', fontWeight: '700', color: '#0A0D1C', marginBottom: '16px' },
    subTitle: { fontSize: '18px', fontWeight: '700', color: '#0A0D1C', margin: '32px 0 12px' },
    text: { fontSize: '16px', lineHeight: '1.8', color: '#3D3F52', marginBottom: '16px' },
    code: { background: 'rgba(99,102,241,.08)', color: '#6366f1', padding: '2px 8px', borderRadius: '4px', fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: '0.9em' },
    infoBox: { background: '#fff', border: '1px solid #E4E5EF', borderLeft: '4px solid #6366f1', borderRadius: '8px', padding: '16px 20px', fontSize: '15px', color: '#0A0D1C' },
    codeBlock: { background: '#0d1117', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(10,13,28,.10)', marginBottom: '16px' },
    codeHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,.08)' },
    codeLang: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6366f1', background: 'rgba(99,102,241,.15)', padding: '3px 10px', borderRadius: '6px' },
    statusBadge: { fontSize: '11px', fontWeight: '600', color: '#22c55e', background: 'rgba(34,197,94,.15)', padding: '3px 10px', borderRadius: '6px' },
    pre: { margin: 0, padding: '20px', overflowX: 'auto', fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: '13px', lineHeight: '1.7', color: '#e6edf3' },
    endpointBadge: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
    methodBadge: { background: '#22c55e', color: '#fff', fontWeight: '700', fontSize: '13px', padding: '6px 14px', borderRadius: '6px', fontFamily: "'JetBrains Mono', monospace" },
    endpointUrl: { fontFamily: "'JetBrains Mono', monospace", fontSize: '16px', color: '#0A0D1C' },
    table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(10,13,28,.04)', border: '1px solid #E4E5EF', marginBottom: '16px' },
    th: { textAlign: 'left', padding: '12px 16px', background: '#f8f9fc', fontWeight: '600', fontSize: '13px', color: '#0A0D1C', borderBottom: '1px solid #E4E5EF' },
    td: { padding: '12px 16px', fontSize: '14px', color: '#3D3F52', borderBottom: '1px solid #f0f1f3' },
    specLinks: { display: 'flex', gap: '12px' },
    specLink: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #E4E5EF', padding: '12px 24px', borderRadius: '10px', color: '#0A0D1C', textDecoration: 'none', fontWeight: '600', fontSize: '15px', transition: 'box-shadow 0.2s' },
    ctaSection: { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', padding: '80px 24px', textAlign: 'center' },
    ctaTitle: { fontSize: '36px', fontWeight: '700', color: '#fff', marginBottom: '16px', fontFamily: "'Fraunces', serif" },
    ctaSub: { fontSize: '18px', color: 'rgba(255,255,255,.9)', marginBottom: '32px' },
    ctaButton: { background: '#fff', color: '#6366f1', border: 'none', padding: '16px 40px', borderRadius: '30px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
    footer: { background: '#fff', padding: '40px 24px', borderTop: '1px solid #E4E5EF' },
    footerInner: { maxWidth: '1200px', margin: '0 auto', textAlign: 'center' },
    footerLinksRow: { display: 'flex', gap: '24px', justifyContent: 'center', marginBottom: '16px' },
    footerLink: { color: '#8B8FA8', textDecoration: 'none', fontSize: '14px' },
    copyright: { fontSize: '14px', color: '#8B8FA8' },
};

export default ApiDocsPage;
